import { heapTrackProperties } from '@m/utils/heap';

import { getSiteManagerUrl } from './parkNowHeap';
import { getReservationFunnelContext } from './reservationFunnelContext';

/**
 * Heap funnel for monthly parking: toggle → search → pin → details → directions → review → checkout.
 * Hourly discovery events stay separate so this funnel only counts monthly mode.
 */

type HeapProps = Record<string, string | number | boolean | undefined>;

const withFunnel = (props: HeapProps): HeapProps => ({
  ...getReservationFunnelContext(),
  ...props,
  timestamp: Date.now(),
});

const track = (event: string, props: HeapProps) => {
  heapTrackProperties(event, withFunnel(props));
};

const siteProps = (siteId: number | string, siteName?: string) => ({
  siteId,
  siteManagerUrl: getSiteManagerUrl(siteId),
  siteName: siteName || '',
});

/** User switches the Hourly/Daily | Monthly control to Monthly. */
export const trackMonthlyParkingModeSelected = ({ surface }: { surface: 'home' | 'park_now' }) => {
  track('MonthlyParking:Mode:Selected', { surface });
};

/** User commits a location search while Monthly is selected. */
export const trackMonthlyParkingLocationSearch = ({
  address,
  latitude,
  longitude,
  surface,
}: {
  address: string;
  latitude: number;
  longitude: number;
  surface: 'home' | 'map';
}) => {
  track('MonthlyParking:Location:Search', { address, latitude, longitude, surface });
};

/** User selects a location pin while Monthly is selected. */
export const trackMonthlyParkingPinClick = ({
  latitude,
  longitude,
  siteId,
  siteName,
}: {
  latitude: number;
  longitude: number;
  siteId: number | string;
  siteName?: string;
}) => {
  track('MonthlyParking:Pin:Click', {
    latitude,
    longitude,
    ...siteProps(siteId, siteName),
  });
};

/** User opens the location sheet from Details or Book now while Monthly is selected. */
export const trackMonthlyParkingDetailsViewed = ({
  siteId,
  siteName,
  source,
}: {
  siteId: number | string;
  siteName?: string;
  source: 'desktop' | 'list' | 'map';
}) => {
  track('MonthlyParking:Details:Viewed', {
    source,
    ...siteProps(siteId, siteName),
  });
};

/** User taps the directions control while Monthly is selected. */
export const trackMonthlyParkingDirectionsClick = ({
  address,
  platform,
  siteId,
  siteName,
}: {
  address: string;
  platform: 'apple_maps' | 'google_maps';
  siteId: number | string;
  siteName?: string;
}) => {
  track('MonthlyParking:Directions:Click', {
    address,
    platform,
    ...siteProps(siteId, siteName),
  });
};

/** Review subscription sheet is shown. */
export const trackMonthlyParkingReviewViewed = ({
  planId,
  siteId,
  siteName,
}: {
  planId: number | string;
  siteId: number | string;
  siteName?: string;
}) => {
  track('MonthlyParking:Review:Viewed', {
    planId,
    ...siteProps(siteId, siteName),
  });
};

/** Subscription purchase succeeds. */
export const trackMonthlyParkingCheckoutCompleted = ({
  planId,
  siteId,
  siteName,
}: {
  planId: number | string;
  siteId: number | string;
  siteName?: string;
}) => {
  track('MonthlyParking:Checkout:Completed', {
    planId,
    ...siteProps(siteId, siteName),
  });
};
